/**
 * Admin Team Management — List + invite + edit entry point.
 *
 * The /admin/team page. Replaces ad-hoc SQL for "who has admin access".
 *
 * Sections:
 *   1. Tabs: Active admins · Pending invitations · Deactivated
 *   2. Active admins table — email · full name · role · bundle · sessions · last login · actions
 *   3. Pending invitations table — email · bundle · expires · resend / revoke
 *   4. Top-right "Invite admin" button → InviteAdminModal
 *   5. Click any admin row → AdminProfileDrawer (right-slide panel)
 *   6. Admin accounts (super admins only) → AdminAccountsSection: the Google sign-in
 *      accounts — add, change role, deactivate / reactivate
 *
 * Endpoints:
 *   GET /api/admin/team
 *   GET /api/admin/team/invitations/pending
 *   POST /api/admin/team/invitations
 *   POST /api/admin/team/invitations/:id/revoke
 *   POST /api/admin/team/invitations/:id/resend
 */

import React, { useState } from 'react';
import {
  AlertCircle, CheckCircle2, Mail, MoreHorizontal, Plus, Shield,
  UserCheck, UserX, Users,
} from 'lucide-react';
import { SessionSummary, summaryLabel } from '../me/sessionsLogic';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';
import { InviteAdminModal } from './InviteAdminModal';
import { AdminProfileDrawer } from './AdminProfileDrawer';
import { AdminAccountsSection } from './AdminAccountsSection';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

interface Admin {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  permissions: { capabilities?: string[]; bundle_applied?: string } | null;
  active: boolean;
  two_factor_enabled: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  locked_until: string | null;
  support_from_email: string | null;
  support_from_display_name: string | null;
  timezone: string | null;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  full_name: string | null;
  bundle_name: string;
  role: string;
  invited_at: string;
  expires_at: string;
  resend_count: number;
  invited_by_name: string | null;
  invited_by_email: string | null;
  notes: string | null;
}

type TabKey = 'active' | 'pending' | 'deactivated';

const TAB_LABELS: Record<TabKey, { label: string; icon: React.ReactNode }> = {
  active:      { label: 'Active admins',     icon: <UserCheck className="h-3.5 w-3.5" /> },
  pending:     { label: 'Pending invites',   icon: <Mail className="h-3.5 w-3.5" /> },
  deactivated: { label: 'Deactivated',       icon: <UserX className="h-3.5 w-3.5" /> },
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: '#7C3AED',
  admin:       '#1F6C4F',
  support:     '#0EA5E9',
  viewer:      '#9CA3AF',
};

function AdminTeamListContent() {
  const [tab, setTab] = useState<TabKey>('active');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Users className="h-5 w-5" style={{ color: '#1F6C4F' }} />
            Admin Team
          </h1>
          <p className="mt-1 text-xs text-gray-500">
            Who can log into the admin portal, what they can do, and how they appear to customers.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: '#1F6C4F' }}
        >
          <Plus className="h-4 w-4" />
          Invite admin
        </button>
      </header>

      {/* Google sign-in accounts — renders for super admins only. */}
      <AdminAccountsSection />

      <div className="flex gap-1 border-b border-gray-200">
        {(Object.keys(TAB_LABELS) as TabKey[]).map(k => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition ${
              tab === k
                ? 'border-current text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            style={tab === k ? { borderColor: '#1F6C4F', color: '#1F6C4F' } : {}}
          >
            {TAB_LABELS[k].icon}
            {TAB_LABELS[k].label}
          </button>
        ))}
      </div>

      {tab === 'active' && (
        <AdminsTable includeInactive={false} onRowClick={setSelectedAdminId} />
      )}
      {tab === 'deactivated' && (
        <AdminsTable includeInactive={true} onlyDeactivated onRowClick={setSelectedAdminId} />
      )}
      {tab === 'pending' && <PendingInvitationsTable />}

      <InviteAdminModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      {selectedAdminId && (
        <AdminProfileDrawer
          adminId={selectedAdminId}
          onClose={() => setSelectedAdminId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Admins table (active or deactivated)
// ─────────────────────────────────────────────────────────────────────────

function AdminsTable({
  includeInactive,
  onlyDeactivated = false,
  onRowClick,
}: {
  includeInactive: boolean;
  onlyDeactivated?: boolean;
  onRowClick: (id: string) => void;
}) {
  const { data, isLoading } = useQuery<{ data: Admin[]; count: number }>({
    queryKey: ['admin-team', includeInactive],
    queryFn: () => adminFetch(`/api/admin/team?include_inactive=${includeInactive}`),
  });
  const sessions = useQuery<{ summary: Record<string, SessionSummary> }>({
    queryKey: ['admin-team-sessions-summary'],
    queryFn: () => adminFetch('/api/admin/sessions/summary'),
    retry: false,
  });
  const rows = (data?.data ?? []).filter(a => onlyDeactivated ? !a.active : a.active);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg bg-white py-16 text-center" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
        <Users className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        <div className="text-xs text-gray-500">
          {onlyDeactivated ? 'No deactivated admins.' : 'No active admins to show.'}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
      <table className="w-full text-xs">
        <thead className="border-b border-gray-100">
          <tr className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3 text-left">Admin</th>
            <th className="px-4 py-3 text-left">Role</th>
            <th className="px-4 py-3 text-left">Bundle</th>
            <th className="px-4 py-3 text-center">Caps</th>
            <th className="px-4 py-3 text-left">Sessions</th>
            <th className="px-4 py-3 text-left">Last login</th>
            <th className="px-4 py-3 text-left">Support display name</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(a => (
            <tr
              key={a.id}
              onClick={() => onRowClick(a.id)}
              className="cursor-pointer border-b border-gray-50 hover:bg-gray-50/60"
            >
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{a.full_name || '—'}</div>
                <div className="text-[11px] text-gray-500">{a.email}</div>
              </td>
              <td className="px-4 py-3">
                <span
                  className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                  style={{ backgroundColor: ROLE_COLORS[a.role] || '#9CA3AF' }}
                >
                  {a.role.replace('_', ' ')}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-700">
                {a.permissions?.bundle_applied || <span className="text-gray-400 italic">custom</span>}
              </td>
              <td className="px-4 py-3 text-center font-mono text-gray-700">
                {a.permissions?.capabilities?.length ?? 0}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {sessions.data ? summaryLabel(sessions.data.summary[a.id]) : '—'}
              </td>
              <td className="px-4 py-3 text-gray-500">
                {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : '—'}
              </td>
              <td className="px-4 py-3 text-gray-700">
                {a.support_from_display_name || <span className="text-gray-400 italic">"Coach Cedar Support"</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pending invitations
// ─────────────────────────────────────────────────────────────────────────

function PendingInvitationsTable() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ data: PendingInvitation[] }>({
    queryKey: ['admin-team-pending'],
    queryFn: () => adminFetch('/api/admin/team/invitations/pending'),
  });
  const rows = data?.data ?? [];

  const resend = useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/admin/team/invitations/${id}/resend`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-team-pending'] }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/admin/team/invitations/${id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Revoked via admin portal' }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-team-pending'] }),
  });

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg bg-white py-16 text-center" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
        <Mail className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        <div className="text-xs text-gray-500">No pending invitations.</div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
      <table className="w-full text-xs">
        <thead className="border-b border-gray-100">
          <tr className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3 text-left">Invitee</th>
            <th className="px-4 py-3 text-left">Bundle</th>
            <th className="px-4 py-3 text-left">Invited by</th>
            <th className="px-4 py-3 text-left">Invited</th>
            <th className="px-4 py-3 text-left">Expires</th>
            <th className="px-4 py-3 text-center">Resends</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(inv => (
            <tr key={inv.id} className="border-b border-gray-50">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{inv.full_name || inv.email}</div>
                {inv.full_name && <div className="text-[11px] text-gray-500">{inv.email}</div>}
              </td>
              <td className="px-4 py-3">{inv.bundle_name}</td>
              <td className="px-4 py-3 text-gray-700">{inv.invited_by_name || inv.invited_by_email || '—'}</td>
              <td className="px-4 py-3 text-gray-500">{new Date(inv.invited_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-gray-500">{new Date(inv.expires_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-center text-gray-600">{inv.resend_count}</td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => resend.mutate(inv.id)}
                  disabled={resend.isPending}
                  className="mr-2 text-[11px] font-medium hover:underline"
                  style={{ color: '#1F6C4F' }}
                >
                  Resend
                </button>
                <button
                  onClick={() => { if (confirm(`Revoke invitation to ${inv.email}?`)) revoke.mutate(inv.id); }}
                  disabled={revoke.isPending}
                  className="text-[11px] font-medium text-red-600 hover:underline"
                >
                  Revoke
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Default export wrapped in QueryClientProvider
// ─────────────────────────────────────────────────────────────────────────

export const AdminTeamList: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AdminTeamListContent />
  </QueryClientProvider>
);

export default AdminTeamList;
